import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWarehouseLocal } from '@/hooks/useWarehouseLocal';
import { apiService } from '@/services/api';
import { ProductOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import './WarehouseAlertSettings.scss';

interface DraftState {
  minThreshold: string;
  hideThreshold: string;
}

const WarehouseAlertSettings: React.FC = () => {
  const { t } = useTranslation();

  const { localId, setLocalId, availableLocals, loading: localLoading } = useWarehouseLocal();

  const [products, setProducts] = useState<ProductOut[]>([]);
  const [drafts, setDrafts] = useState<Record<number, DraftState>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildDraft = (item: ProductOut): DraftState => ({
    minThreshold: String(item.stock.min_threshold),
    hideThreshold: String(item.stock.hide_threshold),
  });

  const loadProducts = useCallback(async () => {
    if (!localId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.getProducts(localId);
      setProducts(data);
      setDrafts(Object.fromEntries(data.map((item) => [item.id, buildDraft(item)])));
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [localId, t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const updateDraft = (productId: number, field: keyof DraftState, value: string) => {
    setDrafts((prev) => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  };

  const handleSave = async (item: ProductOut) => {
    const draft = drafts[item.id];
    if (!draft) return;

    setSavingId(item.id);
    setError(null);
    try {
      const updated = await apiService.updateProductStockThreshold(item.id, {
        min_threshold: Number(draft.minThreshold),
        hide_threshold: Number(draft.hideThreshold),
      });
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setDrafts((prev) => ({ ...prev, [updated.id]: buildDraft(updated) }));
      setSavedId(item.id);
      setTimeout(() => setSavedId((id) => (id === item.id ? null : id)), 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setSavingId(null);
    }
  };

  if (!localLoading && !localId) {
    return (
      <div className="warehouse-alerts">
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noLocalAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="warehouse-alerts">
      <div className="warehouse-alerts__header">
        <h2 className="warehouse-alerts__title">{t('warehouse.alerts.title')}</h2>
        {availableLocals.length > 0 && (
          <SearchableSelect<number>
            className="warehouse-alerts__local-select"
            options={availableLocals.map((local) => ({ value: local.id, label: local.name }))}
            value={localId}
            onChange={(value) => setLocalId(value)}
          />
        )}
      </div>

      {error && <div className="warehouse-alerts__error">{error}</div>}

      {!loading && products.length === 0 ? (
        <Card variant="elevated">
          <p>{t('warehouse.alerts.empty')}</p>
        </Card>
      ) : (
        <div className="warehouse-alerts__list">
          {products.map((item) => {
            const draft = drafts[item.id];
            if (!draft) return null;
            return (
              <Card key={item.id} variant="elevated" className="warehouse-alerts__item">
                <h3>{item.name}</h3>

                <label className="warehouse-alerts__field">
                  {t('warehouse.alerts.minThreshold')}
                  <input
                    type="number"
                    min={0}
                    value={draft.minThreshold}
                    onChange={(e) => updateDraft(item.id, 'minThreshold', e.target.value)}
                  />
                </label>

                <label className="warehouse-alerts__field">
                  {t('warehouse.alerts.globalThreshold')}
                  <input
                    type="number"
                    min={0}
                    value={draft.hideThreshold}
                    onChange={(e) => updateDraft(item.id, 'hideThreshold', e.target.value)}
                  />
                </label>

                <div className="warehouse-alerts__actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSave(item)}
                    disabled={savingId === item.id}
                    loading={savingId === item.id}
                  >
                    {savedId === item.id ? t('warehouse.alerts.saved') : t('warehouse.alerts.save')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WarehouseAlertSettings;
